/**
 * Extracts the filename from a Content-Disposition header value.
 *
 * The Content-Disposition header format can be:
 * - attachment; filename=example.pdf
 * - attachment; filename="example.pdf"
 * - attachment; filename*=UTF-8''example.pdf
 *
 * @param contentDisposition - The Content-Disposition header value
 * @returns The extracted filename, or undefined if not found
 */
export const extractFilenameFromContentDisposition = (
	contentDisposition: string | undefined
): string | undefined => {
	if (!contentDisposition) {
		return undefined
	}

	// Try to match filename*= first (RFC 5987 encoded filename)
	const filenameStarMatch = contentDisposition.match(
		/filename\*=(?:UTF-8''|[^']+'')?([^;]+)/i
	)
	if (filenameStarMatch?.[1]) {
		return decodeURIComponent(filenameStarMatch[1].replaceAll(/['"]/g, ''))
	}

	// Try to match regular filename=
	const filenameMatch = contentDisposition.match(/filename=([^;]+)/i)
	if (filenameMatch?.[1]) {
		return filenameMatch[1].replaceAll(/['"]/g, '').trim()
	}

	return undefined
}

/**
 * Creates a File object from a Blob with the filename extracted from response headers.
 * Falls back to a generic name with the appropriate extension if no filename is found.
 *
 * @param blob - The Blob data
 * @param headers - The response headers object (can contain undefined values)
 * @param fallbackName - The fallback name to use if extraction fails (default: 'download')
 * @returns A File object with the correct filename
 */
export const createFileFromBlobWithHeaders = (
	blob: Blob,
	headers: Record<string, string | undefined>,
	fallbackName = 'download'
): File => {
	const contentDisposition = headers['content-disposition']
	const filename =
		extractFilenameFromContentDisposition(contentDisposition) || fallbackName

	return new File([blob], filename, { type: blob.type })
}
